// frontend/src/components/government/LeaveReimbursementTracker.js
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  Info,
  Shield,
  FileText,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { ShowHiddenToggleButton } from '../UI/ShowHiddenToggleButton';
import LeavePeriodDisplay from '../Leave/LeavePeriodDisplay';
import { LeaveTypeBadge } from '../UI/LeaveTypeBadge';
import ReimbursementSubmissionModal from './ReimbursementSubmissionModal';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';

const formatSGD = (value) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value || 0);

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Leave Types' },
  { value: 'gpcl', label: 'Childcare (CCL / ECCL)' },
  { value: 'gpml', label: 'Maternity (GPML)' },
  { value: 'gppl', label: 'Paternity (GPPL)' },
  { value: 'spl', label: 'Shared Parental (SPL)' },
  { value: 'gpal', label: 'Adoption (GPAL)' },
];

// Maps a tracker claim_status to a StatusBadge status + readable label
const STATUS_META = {
  to_claim: { badge: 'pending', label: 'To Claim' },
  pending: { badge: 'pending', label: 'Draft' },
  submitted: { badge: 'submitted', label: 'Submitted' },
  approved: { badge: 'approved', label: 'Govt Approved' },
  paid: { badge: 'completed', label: 'Reimbursed' },
  rejected: { badge: 'rejected', label: 'Rejected' },
};

const STATUS_BUCKETS = [
  { value: 'all', label: 'All' },
  { value: 'to_claim', label: 'To Claim' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'paid', label: 'Reimbursed' },
  { value: 'overdue', label: 'Overdue' },
];

const inBucket = (entry, bucket) => {
  switch (bucket) {
    case 'to_claim':
      return entry.claim_status === 'to_claim';
    case 'in_progress':
      return ['pending', 'submitted', 'approved'].includes(entry.claim_status);
    case 'paid':
      return entry.claim_status === 'paid';
    case 'overdue':
      return entry.is_overdue;
    default:
      return true;
  }
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].map((y) => ({
  value: String(y),
  label: String(y),
}));

const LeaveReimbursementTracker = () => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(String(currentYear));
  const [statusBucket, setStatusBucket] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmployerPaid, setShowEmployerPaid] = useState(false);

  // Record-claim modal state
  const [recordEntry, setRecordEntry] = useState(null);
  const [recordStatus, setRecordStatus] = useState('submitted');
  const [recordRef, setRecordRef] = useState('');
  const [recordAmount, setRecordAmount] = useState('');

  // MSF submission-detail modal state
  const [detailId, setDetailId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['reimbursement-tracker', { year, showEmployerPaid }],
    queryFn: async () => {
      const res = await staffAPI.getReimbursementTracker({
        year,
        ...(showEmployerPaid ? { include_employer_paid: 'true' } : {}),
      });
      return res.data;
    },
    staleTime: 60 * 1000,
  });

  const recordMutation = useMutation({
    mutationFn: (payload) => staffAPI.recordReimbursementClaim(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursement-tracker'] });
      toast.success('Reimbursement status updated');
      closeRecord();
    },
    onError: (error) => {
      const msg = error?.response?.data?.error || 'Failed to update reimbursement status';
      toast.error(String(msg));
    },
  });

  const summary = data?.summary || {};
  const entries = useMemo(() => data?.entries || [], [data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries.filter((e) => {
      if (!inBucket(e, statusBucket)) return false;
      if (typeFilter !== 'all' && e.reimbursement_type !== typeFilter) return false;
      if (term) {
        const hay = `${e.employee_name || ''} ${e.staff_id || ''} ${e.leave_type_name || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [entries, statusBucket, typeFilter, searchTerm]);

  const openRecord = (entry, presetStatus) => {
    setRecordEntry(entry);
    setRecordStatus(presetStatus);
    setRecordRef(entry.claim_reference_number || '');
    setRecordAmount(entry.amount ? String(entry.amount) : '');
  };

  const closeRecord = () => {
    setRecordEntry(null);
    setRecordRef('');
    setRecordAmount('');
  };

  const submitRecord = (e) => {
    e.preventDefault();
    recordMutation.mutate({
      leave_application_id: recordEntry.leave_application_id,
      claim_status: recordStatus,
      claim_reference_number: recordRef || undefined,
      total_reimbursement_amount: recordAmount ? parseFloat(recordAmount) : undefined,
    });
  };

  const unitText = (entry) => {
    if (!entry.reimbursable_units) return '—';
    const qty = formatLeaveDays(entry.reimbursable_units);
    return `${qty} ${pluralizeDays(entry.reimbursable_units)}`;
  };

  // Which days/weeks of the entitlement this leave falls in (e.g. CCL days 4-6).
  const renderCoverage = (entry) => {
    const label = entry.coverage_label;
    if (!label) return null;
    const isGovt = /govt-paid/i.test(label);
    const isSplit = /split/i.test(label);
    const tone = isSplit
      ? 'bg-amber-50 text-amber-700'
      : isGovt
        ? 'bg-green-50 text-green-700'
        : 'bg-gray-100 text-gray-500';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>
        {label}
      </span>
    );
  };

  const renderDeadline = (entry) => {
    if (!entry.submission_deadline) return <span className="text-gray-400">—</span>;
    if (entry.claim_status === 'paid') {
      return <span className="text-gray-500">{formatDate(entry.submission_deadline)}</span>;
    }
    if (entry.is_overdue) {
      return (
        <span className="inline-flex items-center text-red-600 font-medium">
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
          Overdue
        </span>
      );
    }
    const days = entry.days_until_deadline;
    const soon = typeof days === 'number' && days <= 30;
    return (
      <span className={soon ? 'text-amber-600 font-medium' : 'text-gray-700'}>
        {formatDate(entry.submission_deadline)}
        {typeof days === 'number' && (
          <span className="block text-xs text-gray-400">{days} day(s) left</span>
        )}
      </span>
    );
  };

  const renderActions = (entry) => {
    if (!entry.is_worklist) {
      return <span className="text-xs text-gray-400">No govt claim</span>;
    }
    const isUnclaimed = ['to_claim', 'pending', 'rejected'].includes(entry.claim_status);
    const isSubmittedLike = ['submitted', 'approved'].includes(entry.claim_status);
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn();
    };
    return (
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={stop(() => setDetailId(entry.leave_application_id))}
          className="icon-button-admin"
          title="View MSF submission details"
          aria-label={`View GPL submission details for ${entry.employee_name || entry.staff_id}`}
        >
          <FileText className="w-4 h-4" />
        </button>
        {isUnclaimed && (
          <button
            onClick={stop(() => openRecord(entry, 'submitted'))}
            className="icon-button-admin"
            title="Mark submitted to MSF"
            aria-label={`Mark GPL claim submitted for ${entry.employee_name || entry.staff_id}`}
          >
            <Send className="w-4 h-4" />
          </button>
        )}
        {isSubmittedLike && (
          <button
            onClick={stop(() => openRecord(entry, 'paid'))}
            className="icon-button-admin"
            title="Mark reimbursed by MSF"
            aria-label={`Mark GPL claim reimbursed for ${entry.employee_name || entry.staff_id}`}
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.to_claim_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    To Claim ({summary.to_claim_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.in_progress_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    In Progress ({summary.in_progress_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.reimbursed_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Reimbursed ({summary.reimbursed_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.overdue_count || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Overdue (past 3-mth)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar: status buckets + filters */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
              {STATUS_BUCKETS.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setStatusBucket(b.value)}
                  className={`tab-btn-admin tab-btn-admin-sm ${statusBucket === b.value ? 'active' : ''}`}
                >
                  {b.label}
                  {b.value === 'to_claim' && summary.to_claim_count > 0 && (
                    <CountBadge count={summary.to_claim_count} theme="admin" active={statusBucket === 'to_claim'} />
                  )}
                  {b.value === 'overdue' && summary.overdue_count > 0 && (
                    <CountBadge count={summary.overdue_count} theme="warning" active={statusBucket === 'overdue'} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search by employee or staff ID..."
                theme="admin"
                className="min-w-[200px] max-w-xl"
              />
              <div className="w-48 flex-shrink-0">
                <FormSelect
                  options={TYPE_FILTER_OPTIONS}
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
              <div className="w-28 flex-shrink-0">
                <FormSelect
                  options={YEAR_OPTIONS}
                  value={year}
                  onChange={(v) => setYear(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
              <ShowHiddenToggleButton
                isActive={showEmployerPaid}
                hiddenCount={summary.hidden_employer_paid_count || 0}
                onClick={() => setShowEmployerPaid((v) => !v)}
                titleOff="Show employer-paid leaves (e.g. CCL days 1–3)"
                titleOn="Hide employer-paid leaves"
              />
            </div>
            {!showEmployerPaid && summary.hidden_employer_paid_count > 0 && (
              <p className="text-xs text-gray-500">
                {summary.hidden_employer_paid_count} employer-paid leave
                {summary.hidden_employer_paid_count === 1 ? '' : 's'} hidden — no government
                reimbursement on these (e.g. CCL days 1–3, GPML weeks 1–8).
              </p>
            )}
          </div>

          {/* Tracker list */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border">
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No claims to track</h3>
                <p className="text-sm text-gray-500">
                  No government-reimbursable leave for {year}. Employer-paid-only rows
                  (e.g. CCL days 1–3) are hidden by default — enable &quot;Show employer-paid&quot;
                  to audit the full leave history.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Employee</th>
                      <th className="table-header-cell">Leave Type</th>
                      <th className="table-header-cell">Period</th>
                      <th className="table-header-cell">Govt Reimbursable</th>
                      <th className="table-header-cell">Est. Amount</th>
                      <th className="table-header-cell">Claim Deadline</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filtered.map((entry) => (
                      <tr
                        key={entry.leave_application_id}
                        onClick={entry.is_worklist ? () => setDetailId(entry.leave_application_id) : undefined}
                        className={`table-row${showEmployerPaid && !entry.is_worklist ? ' opacity-60' : ''}${entry.is_worklist ? ' cursor-pointer' : ''}`}
                      >
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{entry.employee_name}</div>
                          <div className="text-xs text-gray-500">{entry.staff_id}</div>
                        </td>
                        <td className="table-cell">
                          <LeaveTypeBadge leaveTypeName={entry.leave_type_name} />
                        </td>
                        <td className="table-cell text-sm text-gray-700">
                          <LeavePeriodDisplay application={entry} totalDays={entry.total_days} showIcon={false} noWrap />
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatLeaveDays(entry.total_days)} {pluralizeDays(entry.total_days)}
                          </div>
                        </td>
                        <td className="table-cell text-sm text-gray-700">
                          <div>{unitText(entry)}</div>
                          <div className="text-xs text-gray-400">@ {formatSGD(entry.rate_cap)}/{entry.unit_label}</div>
                          <div className="mt-1">{renderCoverage(entry)}</div>
                          {entry.coverage_position && (
                            <div className="text-[11px] text-gray-400 mt-0.5">{entry.coverage_position}</div>
                          )}
                        </td>
                        <td className="table-cell font-semibold tabular-nums">
                          {formatSGD(entry.amount)}
                        </td>
                        <td className="table-cell text-sm">{renderDeadline(entry)}</td>
                        <td className="table-cell">
                          <StatusBadge
                            status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                            label={STATUS_META[entry.claim_status]?.label}
                          />
                        </td>
                        <td className="table-cell">{renderActions(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((entry) => (
                  <div
                    key={entry.leave_application_id}
                    onClick={entry.is_worklist ? () => setDetailId(entry.leave_application_id) : undefined}
                    className={`bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow flex flex-col h-full${showEmployerPaid && !entry.is_worklist ? ' opacity-60' : ''}${entry.is_worklist ? ' cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{entry.employee_name}</div>
                        <div className="mt-1">
                          <LeaveTypeBadge leaveTypeName={entry.leave_type_name} size="xs" />
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge
                          status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                          label={STATUS_META[entry.claim_status]?.label}
                        />
                      </div>
                    </div>
                    <LeavePeriodDisplay application={entry} totalDays={entry.total_days} showIcon={false} size="xs" className="text-gray-700 flex-1" />
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      {renderCoverage(entry)}
                      {entry.coverage_position && (
                        <span className="text-[11px] text-gray-400">{entry.coverage_position}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">{unitText(entry)} reimbursable</span>
                      <span className="font-semibold tabular-nums">{formatSGD(entry.amount)}</span>
                    </div>
                    <div className="mt-1 text-xs">Deadline: {renderDeadline(entry)}</div>
                    <div className="card-action-footer justify-end gap-2">
                      {renderActions(entry)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Reference panel */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-2 text-blue-600" />
              How reimbursement is computed (GPL Portal)
            </h4>
            <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
              <li><strong>CCL:</strong> days 1–3 employer-paid; days 4–6 govt-reimbursed, capped S$500/day (up to S$1,500/yr).</li>
              <li><strong>ECCL:</strong> 2 days fully govt-reimbursed, S$500/day (up to S$1,000/yr).</li>
              <li><strong>GPML (Maternity):</strong> 1st/2nd child — govt reimburses <strong>weeks 9–16</strong> (employer pays weeks 1–8), cap <strong>S$20,000</strong>; 3rd+ child — all 16 weeks, cap <strong>S$40,000</strong>. Rate S$2,500/week.</li>
              <li><strong>GPAL:</strong> govt-reimbursed at gross rate, capped <strong>S$10,000 per 4 weeks</strong> (S$2,500/week).</li>
              <li><strong>GPPL / SPL:</strong> govt-reimbursed at gross rate, capped <strong>S$2,500/week</strong>.</li>
              <li>Weekly schemes use a per-day cap of <strong>S$2,500 ÷ working days/week</strong> from each employee's work schedule (e.g. 5-day week → S$500/day).</li>
              <li>Claims must be submitted <strong>within 3 months</strong> of the last day of leave.</li>
            </ul>
            <p className="text-xs text-blue-700 mt-2">
              Amounts are upper-bound estimates at the statutory cap; MSF/MOM confirm the exact
              reimbursement from CPF wage data. Source: profamilyleave.msf.gov.sg
            </p>
          </div>
        </>
      )}

      {/* Record claim modal */}
      <Modal
        isOpen={!!recordEntry}
        onClose={closeRecord}
        title={recordStatus === 'paid' ? 'Mark Reimbursement Received' : 'Mark Claim Submitted'}
        size="md"
        theme="admin"
        footer={
          <>
            <button type="button" onClick={closeRecord} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              type="submit"
              form="record-claim-form"
              disabled={recordMutation.isPending}
              className="btn btn-success"
            >
              {recordMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {recordEntry && (
          <form id="record-claim-form" onSubmit={submitRecord} className="space-y-4">
            <div className="rounded-2xl bg-gray-50 p-4 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-red-600" />
                <span className="font-medium text-gray-900">{recordEntry.employee_name}</span>
                <span className="text-gray-500">({recordEntry.staff_id})</span>
              </div>
              <div className="text-gray-600 flex items-center gap-2 flex-wrap">
                <LeaveTypeBadge leaveTypeName={recordEntry.leave_type_name} size="xs" />
                <span className="text-gray-400">·</span>
                <LeavePeriodDisplay application={recordEntry} totalDays={recordEntry.total_days} showIcon={false} size="xs" />
              </div>
              <div className="text-gray-600 mt-1">{recordEntry.basis}</div>
            </div>

            <div className="form-group">
              <label className="form-label">Claim Reference Number</label>
              <input
                type="text"
                placeholder="e.g., GPL-2026-001234"
                value={recordRef}
                onChange={(e) => setRecordRef(e.target.value)}
                className="input-admin w-full"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {recordStatus === 'paid' ? 'Amount Reimbursed (S$)' : 'Claim Amount (S$)'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={String(recordEntry.estimated_amount)}
                value={recordAmount}
                onChange={(e) => setRecordAmount(e.target.value)}
                className="input-admin w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Defaults to the estimated {formatSGD(recordEntry.estimated_amount)} if left blank.
              </p>
            </div>
          </form>
        )}
      </Modal>

      {/* MSF submission-detail modal */}
      <ReimbursementSubmissionModal
        leaveApplicationId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default LeaveReimbursementTracker;
